import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, Sparkles, Loader2, Star, Trash2, RefreshCw, Wand2, Plus, ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { MarkdownField } from "@/components/markdown-field";
import { unwrapMarkdown } from "@/lib/unwrap-markdown";
import { toast } from "sonner";
import {
  getBonusProject, updateBonusProject,
  generateBonusIdeas, regenerateBonusIdea, updateBonusItem, deleteBonusItem, addManualBonusItem,
  generateBonusContent, generateSalesCopy, rateBonusInteraction,
} from "@/lib/bonus-builder.functions";

const STAGES = ["setup", "ideas", "content", "delivery"] as const;
type Stage = typeof STAGES[number];
const STAGE_TITLES: Record<Stage, string> = {
  setup: "1. Project Setup",
  ideas: "2. Idea Generation",
  content: "3. Content Generation",
  delivery: "4. Sales Copy",
};

function normalizeStage(value: unknown): Stage {
  return STAGES.includes(value as Stage) ? (value as Stage) : value === "complete" ? "delivery" : "setup";
}

function parseStage(value: unknown): Stage | undefined {
  return STAGES.includes(value as Stage) ? (value as Stage) : undefined;
}

export const Route = createFileRoute("/_authenticated/bonus-builder/$projectId")({
  validateSearch: (search: Record<string, unknown>) => ({
    stage: parseStage(search.stage),
  }),
  component: BonusProjectWizard,
});

function BonusProjectWizard() {
  const { projectId } = Route.useParams();
  const { stage: requestedStage } = Route.useSearch();
  const navigate = useNavigate();
  const get = useServerFn(getBonusProject);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["bonus-project", projectId],
    queryFn: () => get({ data: { id: projectId } }),
  });

  if (isLoading || !data) {
    return <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const savedStage = normalizeStage(data.project.stage);
  const stage = requestedStage ?? savedStage;
  const savedStageIdx = STAGES.indexOf(savedStage);
  const goToStage = (nextStage: Stage) => {
    navigate({ to: "/bonus-builder/$projectId", params: { projectId }, search: { stage: nextStage } });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <Link to="/bonus-builder" className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to projects
        </Link>
        <h1 className="text-2xl font-semibold mt-2">{data.project.target_program_name || "Untitled"}</h1>
      </div>

      <div className="flex gap-2" role="tablist" aria-label="Bonus builder stages">
        {STAGES.map((s, i) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={stage === s}
            onClick={() => goToStage(s)}
            className={`flex-1 px-3 py-2 rounded-md text-sm border text-left transition-colors hover:border-primary/50 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              stage === s
                ? "bg-primary text-primary-foreground border-primary"
                : i <= savedStageIdx
                  ? "bg-primary/10 border-primary/30 text-foreground"
                  : "bg-muted/30 border-border text-muted-foreground"
            }`}
          >
            {STAGE_TITLES[s]}
          </button>
        ))}
      </div>

      {stage === "setup" && <StageSetup project={data.project} refetch={refetch} onStageChange={goToStage} />}
      {stage === "ideas" && <StageIdeas project={data.project} items={data.items} refetch={refetch} onStageChange={goToStage} />}
      {stage === "content" && <StageContent project={data.project} items={data.items} refetch={refetch} onStageChange={goToStage} />}
      {stage === "delivery" && <StageSalesCopy project={data.project} items={data.items} refetch={refetch} onStageChange={goToStage} />}
    </div>
  );
}

// ============================================================================
// STAGE 1, SETUP
// ============================================================================
function StageSetup({ project, refetch, onStageChange }: any) {
  const update = useServerFn(updateBonusProject);
  const generate = useServerFn(generateBonusIdeas);
  const [form, setForm] = useState({
    target_program_name: project.target_program_name ?? "",
    audience_description: project.audience_description ?? "",
    user_expertise: project.user_expertise ?? "",
    offer_price_point: project.offer_price_point ?? "",
    bonus_count: project.bonus_count ?? 5,
    target_perceived_value: project.target_perceived_value ?? 2000,
    user_is_recruiting_affiliates: !!project.user_is_recruiting_affiliates,
    user_allows_white_label: !!project.user_allows_white_label,
  });

  const save = useMutation({
    mutationFn: async () => update({ data: { id: project.id, patch: form } }),
    onSuccess: () => { toast.success("Saved"); refetch(); },
  });

  const gen = useMutation({
    mutationFn: async () => {
      await update({ data: { id: project.id, patch: form } });
      return generate({ data: { project_id: project.id } });
    },
    onSuccess: () => { toast.success("Ideas generated"); refetch(); onStageChange("ideas"); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <Card>
      <CardHeader><CardTitle>Project Setup</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Program you're promoting</Label>
            <Input value={form.target_program_name} onChange={(e) => setForm({ ...form, target_program_name: e.target.value })} />
          </div>
          <div>
            <Label>Offer price</Label>
            <Input value={form.offer_price_point} onChange={(e) => setForm({ ...form, offer_price_point: e.target.value })} placeholder="$297/mo" />
          </div>
        </div>
        <div>
          <Label>Audience description</Label>
          <Textarea rows={3} value={form.audience_description} onChange={(e) => setForm({ ...form, audience_description: e.target.value })} placeholder="Who is this for? What's their experience level, goals, pains?" />
        </div>
        <div>
          <Label>Your expertise / unique angle</Label>
          <Textarea rows={3} value={form.user_expertise} onChange={(e) => setForm({ ...form, user_expertise: e.target.value })} placeholder="What can you offer that the vendor or other affiliates can't?" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>How many bonuses? (max 5)</Label>
            <Input type="number" min={1} max={5} value={form.bonus_count} onChange={(e) => setForm({ ...form, bonus_count: Math.min(5, Math.max(1, Number(e.target.value) || 5)) })} />
          </div>
          <div>
            <Label>Target total perceived value ($)</Label>
            <Input type="number" value={form.target_perceived_value} onChange={(e) => setForm({ ...form, target_perceived_value: Number(e.target.value) || 0 })} />
          </div>
        </div>
        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label>Recruiting second-tier affiliates?</Label>
              <p className="text-xs text-muted-foreground">Are you recruiting other affiliates to promote this through you?</p>
            </div>
            <Switch
              checked={form.user_is_recruiting_affiliates}
              onCheckedChange={(v) => setForm({ ...form, user_is_recruiting_affiliates: v, user_allows_white_label: v ? form.user_allows_white_label : false })}
            />
          </div>
          {form.user_is_recruiting_affiliates && (
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label>Allow white-label rights?</Label>
                <p className="text-xs text-muted-foreground">Comfortable letting recruited affiliates use your bonuses as their own?</p>
              </div>
              <Switch
                checked={form.user_allows_white_label}
                onCheckedChange={(v) => setForm({ ...form, user_allows_white_label: v })}
              />
            </div>
          )}
        </div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>
          <Button onClick={() => gen.mutate()} disabled={gen.isPending}>
            {gen.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Generate ideas <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// STAGE 2, IDEAS
// ============================================================================
function StageIdeas({ project, items, refetch, onStageChange }: any) {
  const update = useServerFn(updateBonusProject);
  const regen = useServerFn(regenerateBonusIdea);
  const upd = useServerFn(updateBonusItem);
  const del = useServerFn(deleteBonusItem);
  const add = useServerFn(addManualBonusItem);
  const generate = useServerFn(generateBonusIdeas);
  const [adding, setAdding] = useState(false);
  const [manual, setManual] = useState({ title: "", pitch: "", perceived_value: 0, bonus_type: "Custom" });

  const advance = useMutation({
    mutationFn: async () => update({ data: { id: project.id, patch: { stage: "content" } } }),
    onSuccess: () => { toast.success("Moving to content stage"); refetch(); onStageChange("content"); },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Bonus Ideas</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => generate({ data: { project_id: project.id } }).then(() => { toast.success("More ideas added"); refetch(); })}>
            <Plus className="h-4 w-4 mr-1" /> Generate more
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((item: any) => (
          <div key={item.id} className="border rounded-md p-4 space-y-2">
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1">
                <Input className="font-medium" value={item.title} onBlur={(e) => upd({ data: { id: item.id, patch: { title: e.target.value } } })} defaultValue={item.title} key={item.title} />
                <div className="flex gap-2 mt-2 text-xs">
                  <Badge variant="outline">{item.bonus_type}</Badge>
                  <Badge variant="secondary">${Number(item.perceived_value || 0).toLocaleString()}</Badge>
                </div>
                <Textarea className="mt-2" rows={2} defaultValue={item.description} onBlur={(e) => upd({ data: { id: item.id, patch: { description: e.target.value } } })} />
                {item.complement_reasoning && (
                  <p className="text-xs text-muted-foreground mt-2 italic">Why: {item.complement_reasoning}</p>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Button variant="ghost" size="icon" title="Regenerate" onClick={() => regen({ data: { item_id: item.id } }).then(() => { toast.success("Regenerated"); refetch(); })}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" title="Delete" onClick={() => del({ data: { id: item.id } }).then(() => { toast.success("Removed"); refetch(); })}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}

        {adding ? (
          <div className="border rounded-md p-4 space-y-2 bg-muted/20">
            <Input placeholder="Title" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} />
            <Textarea placeholder="Pitch" rows={2} value={manual.pitch} onChange={(e) => setManual({ ...manual, pitch: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Type" value={manual.bonus_type} onChange={(e) => setManual({ ...manual, bonus_type: e.target.value })} />
              <Input type="number" placeholder="Value $" value={manual.perceived_value} onChange={(e) => setManual({ ...manual, perceived_value: Number(e.target.value) || 0 })} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => add({ data: { project_id: project.id, ...manual } }).then(() => { setAdding(false); setManual({ title: "", pitch: "", perceived_value: 0, bonus_type: "Custom" }); refetch(); })}>Add</Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}><Plus className="h-4 w-4 mr-1" /> Add manual bonus</Button>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => update({ data: { id: project.id, patch: { stage: "setup" } } }).then(() => { refetch(); onStageChange("setup"); })}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Button onClick={() => advance.mutate()} disabled={!items.length}>
            Next: Content <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// STAGE 3, CONTENT
// ============================================================================
function StageContent({ project, items, refetch, onStageChange }: any) {
  const update = useServerFn(updateBonusProject);
  const gen = useServerFn(generateBonusContent);
  const upd = useServerFn(updateBonusItem);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Record<string, string>>({});

  const runGen = async (id: string, refine?: string) => {
    setBusyId(id);
    try {
      await gen({ data: { item_id: id, refine_feedback: refine } });
      toast.success(refine ? "Refined" : "Content generated");
      refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>Content Generation</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {items.map((item: any) => (
          <div key={item.id} className="border rounded-md p-4 space-y-2">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium">{item.title}</h3>
                <p className="text-xs text-muted-foreground">{item.bonus_type} · ${Number(item.perceived_value || 0).toLocaleString()}</p>
              </div>
              <Button size="sm" disabled={busyId === item.id} onClick={() => runGen(item.id)}>
                {busyId === item.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                {item.outline ? "Regenerate" : "Generate deliverable"}
              </Button>
            </div>
            {item.outline && (
              <div className="space-y-3">
                <MarkdownField
                  label="Outline"
                  value={item.outline}
                  fieldKey="outline"
                  minHeight={220}
                  onSave={async (next) => { await upd({ data: { id: item.id, patch: { outline: next } } }); refetch(); }}
                />
                {item.script && (
                  <MarkdownField
                    label="Script"
                    value={item.script}
                    fieldKey="script"
                    minHeight={180}
                    onSave={async (next) => { await upd({ data: { id: item.id, patch: { script: next } } }); refetch(); }}
                  />
                )}
                {item.delivery_notes && (
                  <MarkdownField
                    label="Delivery notes"
                    value={item.delivery_notes}
                    fieldKey="delivery_notes"
                    minHeight={100}
                    onSave={async (next) => { await upd({ data: { id: item.id, patch: { delivery_notes: next } } }); refetch(); }}
                  />
                )}
                <div className="flex gap-2">
                  <Input placeholder="Refine: what should change?" value={feedback[item.id] ?? ""} onChange={(e) => setFeedback({ ...feedback, [item.id]: e.target.value })} />
                  <Button size="sm" variant="outline" disabled={busyId === item.id || !feedback[item.id]?.trim()} onClick={() => runGen(item.id, feedback[item.id])}>Refine</Button>
                </div>
              </div>
            )}
          </div>
        ))}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => update({ data: { id: project.id, patch: { stage: "ideas" } } }).then(() => { refetch(); onStageChange("ideas"); })}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Button onClick={() => update({ data: { id: project.id, patch: { stage: "delivery" } } }).then(() => { refetch(); onStageChange("delivery"); })}>
            Next: Sales Copy <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// STAGE 4, SALES COPY
// ============================================================================
function StageSalesCopy({ project, refetch, onStageChange }: any) {
  const update = useServerFn(updateBonusProject);
  const gen = useServerFn(generateSalesCopy);
  const rate = useServerFn(rateBonusInteraction);
  const [rating, setRating] = useState<number>(0);
  const [fb, setFb] = useState("");
  const usableCopy = (value: unknown) => {
    const text = String(value ?? "").trim();
    return text.startsWith("Regenerate sales copy to create") ? "" : text;
  };
  const salesPageCopy = unwrapMarkdown(project.sales_page_copy, "sales_page_copy");
  const revealEmailCopy = unwrapMarkdown(usableCopy(project.email_reveal_copy) || project.sales_page_copy, "email_reveal_copy");
  const socialPostCopy = unwrapMarkdown(usableCopy(project.social_post_copy) || project.sales_page_copy, "social_post_copy");
  const closeCartCopy = unwrapMarkdown(usableCopy(project.close_cart_copy) || project.sales_page_copy, "close_cart_copy");
  const recruitmentPostCopy = unwrapMarkdown(project.recruitment_post_copy, "recruitment_post_copy");

  const run = useMutation({
    mutationFn: async () => gen({ data: { project_id: project.id } }),
    onSuccess: () => { toast.success("Sales copy generated"); refetch(); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const copy = (txt: string) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Sales Copy</CardTitle>
        <Button onClick={() => run.mutate()} disabled={run.isPending}>
          {run.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {project.sales_page_copy ? "Regenerate" : "Generate copy"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {project.stack_title && (
          <div>
            <div className="flex justify-between items-center">
              <Label>Stack title</Label>
              <Button size="sm" variant="ghost" onClick={() => copy(project.stack_title)}><Copy className="h-3 w-3" /></Button>
            </div>
            <Input value={project.stack_title} readOnly />
          </div>
        )}
        {salesPageCopy && (
          <MarkdownField
            label="Sales page section"
            value={salesPageCopy}
            fieldKey="sales_page_copy"
            minHeight={320}
            onSave={async (next) => { await update({ data: { id: project.id, patch: { sales_page_copy: next } } }); refetch(); }}
          />
        )}
        {revealEmailCopy && (
          <MarkdownField
            label="Reveal email"
            value={revealEmailCopy}
            fieldKey="email_reveal_copy"
            minHeight={200}
            onSave={async (next) => { await update({ data: { id: project.id, patch: { email_reveal_copy: next } } }); refetch(); }}
          />
        )}
        {socialPostCopy && (
          <MarkdownField
            label="Social post"
            value={socialPostCopy}
            fieldKey="social_post_copy"
            minHeight={160}
            onSave={async (next) => { await update({ data: { id: project.id, patch: { social_post_copy: next } } }); refetch(); }}
          />
        )}
        {closeCartCopy && (
          <MarkdownField
            label="Close-cart push email"
            value={closeCartCopy}
            fieldKey="close_cart_copy"
            minHeight={180}
            onSave={async (next) => { await update({ data: { id: project.id, patch: { close_cart_copy: next } } }); refetch(); }}
          />
        )}
        {recruitmentPostCopy && (
          <MarkdownField
            label="Affiliate recruitment post"
            value={recruitmentPostCopy}
            fieldKey="recruitment_post_copy"
            minHeight={160}
            onSave={async (next) => { await update({ data: { id: project.id, patch: { recruitment_post_copy: next } } }); refetch(); }}
          />
        )}
        {project.stack_perceived_value && (
          <p className="text-sm text-muted-foreground">Total stack perceived value: <strong>${Number(project.stack_perceived_value).toLocaleString()}</strong></p>
        )}

        {project.sales_page_copy && (
          <div className="border-t pt-4 space-y-2">
            <Label>Rate this output (learning loop)</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} className={n <= rating ? "text-amber-500" : "text-muted-foreground"}>
                  <Star className={`h-5 w-5 ${n <= rating ? "fill-current" : ""}`} />
                </button>
              ))}
            </div>
            <Textarea rows={2} placeholder="Optional feedback…" value={fb} onChange={(e) => setFb(e.target.value)} />
            <Button size="sm" variant="outline" disabled={!rating} onClick={() => rate({ data: { project_id: project.id, interaction_type: "generate_sales_copy", rating, feedback: fb, kept: true } }).then(() => toast.success("Thanks for the feedback"))}>
              Submit rating
            </Button>
          </div>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => update({ data: { id: project.id, patch: { stage: "content" } } }).then(() => { refetch(); onStageChange("content"); })}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Button onClick={() => update({ data: { id: project.id, patch: { stage: "complete", status: "completed", completed_at: new Date().toISOString() } } }).then(() => { refetch(); onStageChange("delivery"); })}>
            Mark complete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
